import CloseIcon from '@mui/icons-material/Close';
import RedeemOutlinedIcon from '@mui/icons-material/RedeemOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Report } from 'notiflix';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import styled from 'styled-components';
import {
    calculateWithholding,
    createPrizePayout,
} from '../../../data/api/prizePayouts/PrizePayoutRoutes';
import useIndex from '../../../hooks/useIndex';
import validErrors from '../../../hooks/validErrors';

const PAYMENT_METHODS = [
    { value: 'EFECTIVO', label: 'Efectivo' },
    { value: 'TRANSFERENCIA', label: 'Transferencia bancaria' },
    { value: 'NEQUI', label: 'Nequi' },
    { value: 'DAVIPLATA', label: 'Daviplata' },
];

const fmtCOP = (cents) => {
    const value = Number(cents ?? 0) / 100;
    return value.toLocaleString('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
    });
};

const numberFieldProps = (extra = {}) => ({
    type: 'number',
    onWheel: (e) => e.target.blur(),
    onKeyDown: (e) => {
        if (['-', 'e', 'E', '+'].includes(e.key)) e.preventDefault();
    },
    inputProps: { min: 1, step: 1000, ...extra },
});

/**
 * Extrae el valor en COP del primer premio en dinero del Winners.
 * Formato esperado en `winners.prizes` (JSON):
 *   [{ "prize": "$50.000 COP" }, { "prize": "5 Jugadas" }, ...]
 * Devuelve número en COP (no centavos) o null si no hay premio en dinero.
 */
const extractMoneyPrizeCop = (prizesRaw) => {
    if (!prizesRaw) return null;
    let prizes;
    try {
        prizes = typeof prizesRaw === 'string' ? JSON.parse(prizesRaw) : prizesRaw;
    } catch {
        return null;
    }
    if (!Array.isArray(prizes)) return null;
    for (const p of prizes) {
        const text = p?.prize ?? '';
        if (typeof text === 'string' && text.includes('COP')) {
            const digits = text.replace(/[^\d]/g, '');
            if (digits) return parseInt(digits, 10);
        }
    }
    return null;
};

const todayPlusBusinessDays = (days = 10) => {
    const d = new Date();
    let added = 0;
    while (added < days) {
        d.setDate(d.getDate() + 1);
        const day = d.getDay();
        if (day !== 0 && day !== 6) added++;
    }
    return d.toISOString().slice(0, 10);
};

const InitiatePayoutModal = ({ open, onClose, winner, onCreated }) => {
    const { token } = useIndex();
    const [submitting, setSubmitting] = useState(false);
    const [breakdown, setBreakdown] = useState(null);
    const [calcLoading, setCalcLoading] = useState(false);

    const initialGrossCop = useMemo(() => extractMoneyPrizeCop(winner?.prizes) ?? 0, [winner]);
    const [grossCop, setGrossCop] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('EFECTIVO');
    const [paymentReference, setPaymentReference] = useState('');
    const [paymentAccountHolder, setPaymentAccountHolder] = useState('');
    const [deadlineAt, setDeadlineAt] = useState(todayPlusBusinessDays(10));
    const [notes, setNotes] = useState('');

    // Reset al abrir el modal con un nuevo winner.
    useEffect(() => {
        if (!open) return;
        setGrossCop(initialGrossCop || 0);
        setPaymentMethod('EFECTIVO');
        setPaymentReference('');
        setPaymentAccountHolder('');
        setDeadlineAt(todayPlusBusinessDays(10));
        setNotes('');
        setBreakdown(null);
    }, [open, initialGrossCop]);

    // Recalcula la retefuente cada vez que cambia el monto bruto (con
    // un debounce corto para no machacar el endpoint en cada tecla).
    const grossCents = (Number(grossCop) || 0) * 100;
    useDebounce(async () => {
        if (!open) return;
        if (!grossCents || grossCents < 100) {
            setBreakdown(null);
            return;
        }
        setCalcLoading(true);
        try {
            const { data } = await calculateWithholding(token, grossCents);
            if (data?.success) setBreakdown(data.data);
        } catch (error) {
            // Silencioso: el preview es accesorio.
        } finally {
            setCalcLoading(false);
        }
    }, 350, [grossCents, open]);

    const canSubmit =
        !submitting &&
        winner &&
        grossCents >= 100 &&
        !!paymentMethod &&
        !!deadlineAt;

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;

        setSubmitting(true);
        try {
            const { data } = await createPrizePayout(token, {
                winner_uuid: winner.uuid,
                gross_amount: grossCents,
                payment_method: paymentMethod,
                payment_reference: paymentReference || null,
                payment_account_holder: paymentAccountHolder || null,
                deadline_at: deadlineAt,
                notes: notes || null,
            });
            if (data?.success) {
                Report.success('Pago iniciado', data?.message ?? 'Pago de premio iniciado correctamente.', 'Aceptar');
                onCreated?.(data?.data);
                onClose?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={() => !submitting && onClose?.()}>
            <Center>
                <Card sx={{ width: '100%', maxWidth: 640, maxHeight: '92vh', overflow: 'auto' }}>
                    <CardContent>
                        <Header>
                            <Box className="title-wrap">
                                <Box className="icon-box">
                                    <RedeemOutlinedIcon />
                                </Box>
                                <Box>
                                    <Typography variant="h6">Iniciar pago de premio</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Se calculará automáticamente la retención en la fuente según DIAN.
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => !submitting && onClose?.()} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Header>

                        {winner && (
                            <WinnerBox>
                                <Typography variant="overline" color="text.secondary">Ganador</Typography>
                                <Typography sx={{ fontWeight: 900, color: '#10285f' }}>
                                    {winner.user_name}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                    <Chip size="small" label={`Doc: ${winner.document ?? '-'}`} />
                                    <Chip size="small" label={`Hits: ${winner.result_hits}`} />
                                    <Chip size="small" variant="outlined" label={`Juego: ${winner.game_code}`} />
                                </Box>
                            </WinnerBox>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <form onSubmit={handleSubmit} noValidate>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Valor bruto (COP)"
                                        helperText="Mínimo $5.000 COP"
                                        {...numberFieldProps({ min: 5000, step: 1000 })}
                                        value={grossCop}
                                        onChange={(e) => setGrossCop(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Método de pago</InputLabel>
                                        <Select
                                            label="Método de pago"
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                        >
                                            {PAYMENT_METHODS.map((m) => (
                                                <MenuItem key={m.value} value={m.value}>
                                                    {m.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Fecha límite de entrega"
                                        InputLabelProps={{ shrink: true }}
                                        value={deadlineAt}
                                        onChange={(e) => setDeadlineAt(e.target.value)}
                                        helperText="Default: 10 días hábiles"
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Titular cuenta destino"
                                        placeholder="(opcional)"
                                        value={paymentAccountHolder}
                                        onChange={(e) => setPaymentAccountHolder(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Referencia de pago"
                                        placeholder="(opcional, p.ej. # cuenta o # transacción)"
                                        value={paymentReference}
                                        onChange={(e) => setPaymentReference(e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={2}
                                        label="Notas (opcional)"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <BreakdownBox $applies={!!breakdown?.applies}>
                                <Typography variant="overline" color="text.secondary">
                                    Desglose financiero {calcLoading && '(calculando...)'}
                                </Typography>
                                <Row>
                                    <span>Bruto</span>
                                    <strong>{fmtCOP(grossCents)}</strong>
                                </Row>
                                <Row>
                                    <span>
                                        Retefuente {breakdown?.applies
                                            ? `(${Math.round((breakdown.applied_rate ?? 0) * 100)}% sobre el premio)`
                                            : '(no aplica)'}
                                    </span>
                                    <strong>− {fmtCOP(breakdown?.withholding ?? 0)}</strong>
                                </Row>
                                <Row className="total">
                                    <span>Neto a entregar</span>
                                    <strong>
                                        {fmtCOP(breakdown?.net ?? grossCents)}
                                    </strong>
                                </Row>
                                {breakdown && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Umbral: {breakdown.threshold_uvt} UVT × {fmtCOP(breakdown.uvt_value_cop * 100)} ={' '}
                                        {fmtCOP(breakdown.threshold_cents)}
                                    </Typography>
                                )}
                            </BreakdownBox>

                            <LoadingButton
                                fullWidth
                                type="submit"
                                variant="contained"
                                loading={submitting}
                                disabled={!canSubmit}
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>Iniciar pago</span>
                            </LoadingButton>
                        </form>
                    </CardContent>
                </Card>
            </Center>
        </Modal>
    );
};

export default InitiatePayoutModal;

const Center = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 92%;
    max-width: 640px;
    outline: none;
`;

const Header = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 0.75rem;

    .title-wrap {
        display: flex;
        align-items: center;
        gap: 0.7rem;
    }

    .icon-box {
        width: 38px;
        height: 38px;
        border-radius: 12px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        color: #10285f;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
`;

const WinnerBox = styled.div`
    padding: 0.7rem 0.85rem;
    border-radius: 12px;
    background: rgba(16, 40, 95, 0.04);
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const BreakdownBox = styled.div`
    margin-top: 1.2rem;
    padding: 0.85rem 1rem;
    border-radius: 12px;
    background: ${({ $applies }) =>
        $applies ? 'rgba(211, 47, 47, 0.06)' : 'rgba(46, 125, 50, 0.06)'};
    border: 1px solid ${({ $applies }) =>
        $applies ? 'rgba(211, 47, 47, 0.22)' : 'rgba(46, 125, 50, 0.22)'};
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 0.2rem 0;
    color: rgba(15, 34, 77, 0.78);

    &.total {
        margin-top: 0.4rem;
        padding-top: 0.5rem;
        border-top: 1px dashed rgba(15, 34, 77, 0.12);
        color: #10285f;
        font-size: 1.05rem;
    }
`;
